import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import baseUrl from "../../../../../api/baseUrl";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import Select from "react-select";
import SimpleToothChartPicker from "./SimpleToothChartPicker";
import {
  hasToothSelection,
  isDateBeforeDay,
  parseLocalDateInput,
  toLocalDateInputValue,
} from "../treatmentform/treatmentFormUtils";
import { toast } from "react-toastify";
import { appointmentIdPayload } from "../../../../../utils/appointmentLinkContext";
import { isInventoryEnabled } from "../../../../../utils/clinicFeatures";
import StageProductsPicker from "./StageProductsPicker";
import {
  resolveReleaseStagesFromSelection,
  resolveStageCategoryFromSelection,
  StageProductLine,
  stageProductsFromItem,
  stageProductsPayload,
} from "./stageProducts";
type FormDataType = {
  treatmentkey: number;
  treatment: string;
  treatments: string;
  treatmentId: string;
  type: string;
  types: string;
  typeId: string;
  subtype: string;
  subtypes: string;
  subtypeId: string;
  toothNos: string;
  toothNo: string;
  prices: number;
  price: number;
  description: string;
  createdAt: Date;
  quantity: number;
  note: string;
  surface: string;
  stage: string;
  didBy?: string; // Make didBy optional
  reason?: string; // Make didBy optional
};

const getStagesList = (stages: any) =>
  Array.isArray(stages) ? stages.map((s) => String(s || "").trim()).filter(Boolean) : [];

const resolveStagesFromSelection = (
  catalog: any[],
  treatmentId: string,
  typeId: string,
  subtypeId: string
) => {
  if (!catalog?.length || !treatmentId) return [];
  const main = catalog.find((item: any) => String(item._id) === String(treatmentId));
  if (!main) return [];

  if (subtypeId && typeId) {
    const typeItem = main.type?.find((t: any) => String(t._id) === String(typeId));
    const subtype = typeItem?.subType?.find((s: any) => String(s._id) === String(subtypeId));
    const subtypeStages = getStagesList(subtype?.stages);
    if (subtypeStages.length) return subtypeStages;
  }

  if (typeId) {
    const typeItem = main.type?.find((t: any) => String(t._id) === String(typeId));
    const typeStages = getStagesList(typeItem?.stages);
    if (typeStages.length) return typeStages;
  }

  return getStagesList(main.stages);
};

const resolveCatalogUnitPrice = (
  main: any,
  typeId: string,
  subtypeId: string
) => {
  if (!main) return 0;

  if (subtypeId && typeId && main.type) {
    const typeItem = main.type.find((t: any) => String(t._id) === typeId);
    const subItem = typeItem?.subType?.find((s: any) => String(s._id) === subtypeId);
    if (subItem?.price != null && subItem.price !== "") {
      return Number(subItem.price) || 0;
    }
  }

  if (typeId && main.type) {
    const typeItem = main.type.find((t: any) => String(t._id) === typeId);
    if (typeItem?.price != null && typeItem.price !== "") {
      return Number(typeItem.price) || 0;
    }
  }

  return Number(main.price ?? 0) || 0;
};

const buildPaidSubTreatmentPayload = (formData: FormDataType) => {
  const unitPrice = Number(formData.prices) || 0;
  const quantity = Number(formData.quantity) || 1;

  return {
    ...formData,
    prices: unitPrice,
    quantity,
    price: unitPrice * quantity,
  };
};

const parseSelectedTeeth = (formData: FormDataType): string[] => {
  if (formData.toothNos) {
    return formData.toothNos
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (formData.toothNo) return [String(formData.toothNo)];
  return [];
};

const applySelectedTeeth = (teeth: string[]) => {
  const first = teeth[0] || "";
  return {
    toothNos: teeth.join(","),
    toothNo: /^\d+$/.test(first) ? first : "",
  };
};

function AddSubTreatmentModel({
  setSubTreatmentOpen,
  TreatmentId,
  setallTeatment,
  allTeatment,
  inline = false,
  inlineRow = false,
  nested = false,
  embedded = false,
  onClose,
  onSaved,
  editItem = null,
}: any) {
  const { ID } = useParams();
  const [searchParams] = useSearchParams();
  const appointmentId = String(searchParams.get("appointmentId") ?? "").trim();
  const isEdit = Boolean(editItem?._id);
  const [openOther, setopenOther] = useState(false);
  const [otherserror, setotherserror] = useState(false);
  const [error] = useState(false);
  const [reasonerror, setreasonerror] = useState(false);
  const [stageerror, setstageerror] = useState(false);
  const [toothError, setToothError] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prefilledParentId = useRef<string | null>(null);
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const initialFormData: FormDataType = {
    treatmentkey: Math.floor(Math.random() * 9) + 1,
    treatment: "",
    treatments: "",
    treatmentId: "",
    type: "",
    types: "",
    typeId: "",
    subtype: "",
    subtypes: "",
    subtypeId: "",
    toothNos: "",
    toothNo: "",
    prices: 0,
    price: 0,
    description: "",
    createdAt: new Date(),
    quantity: 1,
    note: "",
    surface: "",
    stage: "",
    reason: "",
  };

  // Conditionally add didBy field if doctorid is present

  const [formData, setformData] = useState(initialFormData);
  // Products used on a stage the catalog flags as releasing products
  const [stageProducts, setStageProducts] = useState<StageProductLine[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>("nontreatment");
  const [selectedPaid, setSelectedPaid] = useState<string>("nonpaid");

  const resetSubForm = () => {
    isSubmittingRef.current = false;
    setIsSubmitting(false);
    prefilledParentId.current = null;
    setformData({
      ...initialFormData,
      treatmentkey: Math.floor(Math.random() * 9) + 1,
      createdAt: new Date(),
    });
    setStageProducts([]);
    setopenOther(false);
    setotherserror(false);
    setreasonerror(false);
    setstageerror(false);
    setToothError(false);
    setSelectedOption("nontreatment");
    setSelectedPaid("nonpaid");
  };

  const handleInlineClose = () => {
    resetSubForm();
    onClose?.();
    setSubTreatmentOpen?.(false);
  };

  const selectStyles = useMemo(
    () => ({
      control: (base: any, state: any) => ({
        ...base,
        minHeight: 34,
        borderRadius: "8px",
        borderColor: state.isFocused ? "var(--tw-ring-color, rgb(99 102 241))" : "rgb(226 232 240)",
        boxShadow: state.isFocused ? "0 0 0 2px rgb(99 102 241 / 0.2)" : "none",
        "&:hover": { borderColor: "rgb(203 213 225)" },
        backgroundColor: "white",
      }),
      menu: (base: any) => ({
        ...base,
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
        zIndex: 50,
      }),
      menuList: (base: any) => ({ ...base, padding: 0, maxHeight: 220 }),
      option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isSelected ? "rgb(99 102 241 / 0.15)" : state.isFocused ? "rgb(241 245 249)" : "white",
        color: state.isSelected ? "rgb(67 56 202)" : "rgb(30 41 59)",
        cursor: "pointer",
        fontSize: "0.8125rem",
        padding: "6px 12px",
      }),
      placeholder: (base: any) => ({ ...base, color: "rgb(148 163 184)", fontSize: "0.8125rem" }),
      singleValue: (base: any) => ({ ...base, color: "rgb(30 41 59)", fontSize: "0.8125rem" }),
      input: (base: any) => ({ ...base, fontSize: "0.8125rem", color: "rgb(30 41 59)" }),
      indicatorSeparator: () => ({ display: "none" }),
      dropdownIndicator: (base: any) => ({
        ...base,
        color: "rgb(100 116 139)",
        padding: "6px 10px",
        "&:hover": { color: "rgb(71 85 105)" },
      }),
    }),
    []
  );

  const darkSelectStyles = useMemo(
    () => ({
      ...selectStyles,
      control: (base: any, state: any) => ({
        ...selectStyles.control(base, state),
        borderColor: state.isFocused ? "rgb(99 102 241)" : "rgb(71 85 105)",
        backgroundColor: "rgb(51 65 85)",
      }),
      option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isSelected ? "rgb(99 102 241 / 0.3)" : state.isFocused ? "rgb(51 65 85)" : "rgb(51 65 85)",
        color: state.isSelected ? "rgb(199 210 254)" : "rgb(226 232 240)",
      }),
      placeholder: (base: any) => ({ ...base, color: "rgb(148 163 184)" }),
      singleValue: (base: any) => ({ ...base, color: "rgb(226 232 240)" }),
      input: (base: any) => ({ ...base, color: "rgb(226 232 240)" }),
      menu: (base: any) => ({ ...base, backgroundColor: "rgb(51 65 85)" }),
      dropdownIndicator: (base: any) => ({ ...base, color: "rgb(148 163 184)" }),
    }),
    [selectStyles]
  );

  const treatmentOptions = useMemo(() => {
    const list = data?.filter((item: any) => item.status1 === true) || [];
    const others = list.filter((item: any) => (item.treatment || "").trim().toLowerCase() === "others");
    const rest = list.filter((item: any) => (item.treatment || "").trim().toLowerCase() !== "others");
    rest.sort((a: any, b: any) => (a.treatment || "").localeCompare(b.treatment || ""));
    const sorted = [...rest, ...others];
    return sorted.map((item: any) => ({
      value: item._id + "/" + item.treatment + "/" + (item.price ?? 0),
      label: item.treatment,
      price: Number(item.price ?? 0),
    }));
  }, [data]);

  const formatTreatmentOptionLabel = (option: { label: string; price?: number }) => (
    <div className="flex items-center justify-between gap-3 w-full">
      <span className="truncate">{option.label}</span>
      <span className="shrink-0 text-xs font-medium text-slate-500 dark:text-slate-400 tabular-nums">
        Rs {Number(option.price ?? 0).toLocaleString()}
      </span>
    </div>
  );

  const parentTreatment = useMemo(
    () => allTeatment?.find((item: any) => String(item._id) === String(TreatmentId)),
    [allTeatment, TreatmentId]
  );

  const minSubDateValue = useMemo(
    () => toLocalDateInputValue(parentTreatment?.createdAt) || undefined,
    [parentTreatment?.createdAt]
  );

  // Keep case-treatment date from falling earlier than the parent main treatment.
  useEffect(() => {
    if (!parentTreatment?.createdAt) return;
    setformData((prev) => {
      if (!isDateBeforeDay(prev.createdAt, parentTreatment.createdAt)) {
        return prev;
      }
      return {
        ...prev,
        createdAt: parseLocalDateInput(
          toLocalDateInputValue(parentTreatment.createdAt)
        ),
      };
    });
  }, [parentTreatment?.createdAt]);

  const typeOptions = useMemo(() => {
    const isOthers = (formData.treatment || "").trim().toLowerCase() === "others";
    if (!data || isOthers) return [];
    const types =
      data
        ?.filter((item: any) => String(item._id) === String(formData.treatmentId))
        ?.map((filteredItem: any) => filteredItem.type)
        ?.flat() || [];
    return types.map((item: any) => ({
      value: item._id + "/" + item.treatment + "/" + item.price,
      label: item.treatment,
    }));
  }, [data, formData.treatmentId, formData.treatment]);

  const subtypeOptions = useMemo(() => {
    const isOthers = (formData.treatment || "").trim().toLowerCase() === "others";
    if (!data || isOthers) return [];
    const subtypes =
      data
        ?.filter((item: any) => String(item._id) === String(formData.treatmentId))
        ?.map((filteredItem: any) => filteredItem.type)
        ?.flat()
        ?.filter((item: any) => String(item._id) === String(formData.typeId))
        ?.map((filteredItem: any) => filteredItem.subType)
        ?.flat() || [];
    return subtypes.map((item: any) => ({
      value: item._id + "/" + item.treatment + "/" + item.price,
      label: item.treatment,
    }));
  }, [data, formData.treatmentId, formData.typeId, formData.treatment]);

  const catalogTreatmentId = formData.treatmentId || parentTreatment?.treatmentId || "";
  const catalogTypeId = formData.typeId || parentTreatment?.typeId || "";
  const catalogSubtypeId = formData.subtypeId || parentTreatment?.subtypeId || "";

  const availableStages = useMemo(
    () =>
      resolveStagesFromSelection(
        data,
        catalogTreatmentId,
        catalogTypeId,
        catalogSubtypeId
      ),
    [data, catalogTreatmentId, catalogTypeId, catalogSubtypeId]
  );

  const showStagesTab = availableStages.length > 0 && !!catalogTreatmentId;

  const releaseStages = useMemo(
    () =>
      resolveReleaseStagesFromSelection(
        data,
        catalogTreatmentId,
        catalogTypeId,
        catalogSubtypeId
      ),
    [data, catalogTreatmentId, catalogTypeId, catalogSubtypeId]
  );
  // The inventory category the catalog ties this stage to (null: ask for one)
  const stageCategoryLink = useMemo(
    () =>
      resolveStageCategoryFromSelection(
        data,
        catalogTreatmentId,
        catalogTypeId,
        catalogSubtypeId,
        String(formData.stage || "")
      ),
    [data, catalogTreatmentId, catalogTypeId, catalogSubtypeId, formData.stage]
  );
  // Lines a released stage already took from stock; edits move only the difference.
  const releasedStageProducts = useMemo(
    () => (editItem?.productsReleaseId ? stageProductsFromItem(editItem) : undefined),
    [editItem]
  );
  // A stage that has (or had) products keeps showing them, even once all are removed
  const stageUsesProducts =
    selectedOption === "stages" &&
    isInventoryEnabled() &&
    (releaseStages.includes(String(formData.stage || "").trim()) ||
      stageProducts.length > 0 ||
      Boolean(editItem?.products?.length));

  const stageOptions = useMemo(
    () => availableStages.map((stage) => ({ value: stage, label: stage })),
    [availableStages]
  );

  const selectedTreatmentDisplay = useMemo(() => {
    if (!parentTreatment) return "—";
    const parts = [
      parentTreatment.treatment,
      parentTreatment.type,
      parentTreatment.subtype,
    ].filter(Boolean);
    return parts.join(" › ");
  }, [parentTreatment]);

  const tabOptions = useMemo(() => {
    const tabs = [
      { value: "treatment", label: "Treatment" },
      { value: "nontreatment", label: "Non Treatment" },
    ];
    if (showStagesTab) {
      tabs.push({ value: "stages", label: "Stages" });
    }
    return tabs;
  }, [showStagesTab]);

  // Wait for the catalog: before it loads there are no stages, and an edited
  // stage would drop to the Treatment tab with its stage and products cleared
  useEffect(() => {
    if (!data?.length) return;
    if (selectedOption === "stages" && !showStagesTab) {
      setSelectedOption("treatment");
    }
  }, [data, showStagesTab, selectedOption]);

  useEffect(() => {
    if (!data?.length) return;
    if (formData.stage && !availableStages.includes(formData.stage)) {
      setformData((prev) => ({ ...prev, stage: "" }));
    }
  }, [data, availableStages, formData.stage]);

  useEffect(() => {
    baseUrl
      .get("alltreatment/findAllTreatment")
      .then((response) => {
        setData(response.data.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    prefilledParentId.current = null;
  }, [TreatmentId]);

  // Pre-fill from parent treatment row so Stages tab can resolve catalog stages
  useEffect(() => {
    if (editItem?._id) return;
    if (!parentTreatment?.treatmentId || !data?.length) return;
    if (prefilledParentId.current === String(TreatmentId)) return;

    const catalogId = parentTreatment.treatmentId;
    const main: any = data.find((item: any) => String(item._id) === String(catalogId));
    if (!main) return;

    const isOthers = (main.treatment || "").trim().toLowerCase() === "others";
    setopenOther(isOthers);

    let types = "";
    let subtypes = "";
    const typeId = parentTreatment.typeId ? String(parentTreatment.typeId) : "";
    const subtypeId = parentTreatment.subtypeId ? String(parentTreatment.subtypeId) : "";

    if (typeId && main.type) {
      const typeItem = main.type.find((t: any) => String(t._id) === typeId);
      if (typeItem) {
        types = `${typeItem._id}/${typeItem.treatment}/${typeItem.price ?? 0}`;
      }
    }

    if (subtypeId && typeId && main.type) {
      const typeItem = main.type.find((t: any) => String(t._id) === typeId);
      const subItem = typeItem?.subType?.find((s: any) => String(s._id) === subtypeId);
      if (subItem) {
        subtypes = `${subItem._id}/${subItem.treatment}/${subItem.price ?? 0}`;
      }
    }

    const catalogPrice = resolveCatalogUnitPrice(main, typeId, subtypeId);

    setformData((prev) => ({
      ...prev,
      treatments: `${main._id}/${main.treatment}/${main.price ?? 0}`,
      treatmentId: String(catalogId),
      treatment: parentTreatment.treatment || main.treatment,
      type: parentTreatment.type || "",
      types,
      typeId,
      subtype: parentTreatment.subtype || "",
      subtypes,
      subtypeId,
      prices: catalogPrice,
      stage: "",
    }));
    prefilledParentId.current = String(TreatmentId);
  }, [TreatmentId, parentTreatment, data, editItem]);

  // Prefill when editing an existing case/sub treatment (including date)
  useEffect(() => {
    if (!editItem?._id) return;

    const option = editItem.selected_option || "";
    if (option === "treatment_stage") {
      setSelectedOption("stages");
      setSelectedPaid("paid");
    } else if (option === "non_treatment") {
      setSelectedOption("nontreatment");
      setSelectedPaid("nonpaid");
    } else if (option === "treatment_paid") {
      setSelectedOption("treatment");
      setSelectedPaid("paid");
    } else if (option === "treatment_non_paid") {
      setSelectedOption("treatment");
      setSelectedPaid("nonpaid");
    }

    const isOthers =
      Boolean(editItem.description) ||
      (editItem.treatment &&
        !editItem.treatmentId &&
        option !== "treatment_stage");

    const qty = Number(editItem.quantity) || 1;
    const linePrice = Number(editItem.price) || 0;
    const unitPrice =
      editItem.prices != null && editItem.prices !== ""
        ? Number(editItem.prices) || 0
        : qty > 0
          ? linePrice / qty
          : linePrice;

    setopenOther(Boolean(isOthers) && option !== "non_treatment");
    setformData((prev) => ({
      ...prev,
      treatmentkey: editItem.treatmentkey || prev.treatmentkey,
      treatment: editItem.treatment || "",
      treatments: editItem.treatmentId
        ? `${editItem.treatmentId}/${editItem.treatment || ""}/${unitPrice}`
        : prev.treatments,
      treatmentId: editItem.treatmentId ? String(editItem.treatmentId) : "",
      type: editItem.type || "",
      types: editItem.typeId
        ? `${editItem.typeId}/${editItem.type || ""}/${unitPrice}`
        : "",
      typeId: editItem.typeId ? String(editItem.typeId) : "",
      subtype: editItem.subtype || "",
      subtypes: editItem.subtypeId
        ? `${editItem.subtypeId}/${editItem.subtype || ""}/${unitPrice}`
        : "",
      subtypeId: editItem.subtypeId ? String(editItem.subtypeId) : "",
      toothNos: editItem.toothNos || "",
      toothNo: editItem.toothNo != null ? String(editItem.toothNo) : "",
      prices: unitPrice,
      price: linePrice,
      description:
        editItem.description ||
        (isOthers ? editItem.treatment || "" : ""),
      createdAt: editItem.createdAt
        ? new Date(editItem.createdAt)
        : new Date(),
      quantity: qty,
      note: editItem.note || "",
      surface: editItem.surface || "",
      stage: editItem.stage || "",
      reason:
        option === "non_treatment"
          ? editItem.reason || editItem.treatment || ""
          : "",
    }));
    setStageProducts(stageProductsFromItem(editItem));
  }, [editItem]);

  const onchange = (e: any) => {
    const { value, name } = e.target;

    if (name === "treatments") {
      const isOthersFromApi = value && (value.split("/")[1] || "").trim().toLowerCase() === "others";
      if (isOthersFromApi) {
        const separatedArray = value.split("/");
        setopenOther(true);
        setformData((prevState) => ({
          ...prevState,
          [name]: value,
          treatmentId: separatedArray[0],
          treatment: separatedArray[1],
          prices: Number(separatedArray[2]) || 0,
          type: "",
          types: "",
          typeId: "",
          subtype: "",
          subtypes: "",
          subtypeId: "",
          stage: "",
        }));
      } else {
        setopenOther(false);
        const separatedArray = value.split("/");
        setformData((prevState) => ({
          ...prevState,
          [name]: value,
          treatmentId: separatedArray[0],
          treatment: separatedArray[1],
          prices: Number(separatedArray[2]) || 0,
          type: "",
          types: "",
          typeId: "",
          subtype: "",
          subtypes: "",
          subtypeId: "",
          stage: "",
        }));
      }
    } else {
      if ((formData.treatment || "").trim().toLowerCase() === "others") {
        if (name === "types") {
          const separatedArray = value.split("/");
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            typeId: separatedArray[0],
            type: separatedArray[1],
            prices: Number(separatedArray[2]) || 0,
            subtype: "",
            subtypes: "",
            subtypeId: "",
            stage: "",
          }));
        } else if (name === "subtypes") {
          const separatedArray = value.split("/");
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            subtypeId: separatedArray[0],
            subtype: separatedArray[1],
            prices: Number(separatedArray[2]) || 0,
            stage: "",
          }));
        } else if (name === "quantity") {
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            // price: formData.quantity
          }));
        } else if (name === "note") {
          if (textareaRef.current) {
            textareaRef.current.style.height = "auto"; // Reset height
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Set height to fit content
          }
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            // price: formData.quantity
          }));
        } else{
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
          }));
        }
      } else {
        if (name === "types") {
          const separatedArray = value.split("/");
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            typeId: separatedArray[0],
            type: separatedArray[1],
            prices: Number(separatedArray[2]) || 0,
            subtype: "",
            subtypes: "",
            subtypeId: "",
            stage: "",
          }));
        } else if (name === "subtypes") {
          const separatedArray = value.split("/");
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            subtypeId: separatedArray[0],
            subtype: separatedArray[1],
            prices: Number(separatedArray[2]) || 0,
            stage: "",
          }));
        } else if (name === "quantity") {
          setformData((prevState) => ({
            ...prevState,
            [name]: value === "" ? 1 : Number(value) || 1,
          }));
        } else if (name === "prices") {
          setformData((prevState) => ({
            ...prevState,
            [name]: value === "" ? 0 : Number(value) || 0,
          }));
        } else if (name === "note") {
          if (textareaRef.current) {
            textareaRef.current.style.height = "auto"; // Reset height
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Set height to fit content
          }
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
            // price: formData.quantity
          }));
        } else{
          setformData((prevState) => ({
            ...prevState,
            [name]: value,
          }));
        }
      }
    }
  };

  const onsubmit = (e: any) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    setotherserror(false);
    setreasonerror(false);
    setstageerror(false);
    setToothError(false);

    if (!hasToothSelection(formData)) {
      setToothError(true);
      toast.error("Select at least one tooth");
      return;
    }

    if (
      parentTreatment?.createdAt &&
      isDateBeforeDay(formData.createdAt, parentTreatment.createdAt)
    ) {
      toast.error(
        `Case treatment date cannot be earlier than the main treatment (${toLocalDateInputValue(
          parentTreatment.createdAt
        )})`
      );
      return;
    }

    let formDatas = {};
    if (selectedOption === "stages") {
      if (!formData.treatments) {
        setotherserror(true);
        return;
      }
      if (!formData.stage) {
        setstageerror(true);
        return;
      }
      const stagePrice = Number(formData.prices) || 0;
      if (stagePrice < 0) {
        setotherserror(true);
        return;
      }
      formDatas = {
        ...formData,
        selectedOption: "treatment_stage",
        prices: stagePrice,
        price: stagePrice,
        status: "Complete",
        // on a released stage the backend takes or returns the difference
        ...(stageUsesProducts ? { products: stageProductsPayload(stageProducts) } : {}),
      };
    } else if (selectedOption === "nontreatment") {
      if (formData.reason === "") {
        setreasonerror(true);
        return;
      }
      const data = {
        reason: formData.reason,
        treatment: formData.reason,
        treatmentkey: formData.treatmentkey,
        toothNo: formData.toothNo,
        toothNos: formData.toothNos,
        surface: formData.surface,
        note: formData.note,
        createdAt: formData.createdAt,
        selectedOption: "non_treatment",
        treatments: "others",
      };
      formDatas = data;
    } else {
      if ((formData.treatment || "").trim().toLowerCase() === "others") {
        if (formData.description === "") {
          setotherserror(true);
          return;
        }
        if (selectedPaid === "paid" && Number(formData.prices) < 0) {
          setotherserror(true);
          return;
        }
        if (selectedPaid === "nonpaid") {
          formDatas = {
            ...formData,
            treatmentId: formData.treatmentId,
            selectedOption: "treatment_non_paid",
            treatment: formData.description,
          };
        } else {
          formDatas = {
            ...buildPaidSubTreatmentPayload(formData),
            treatmentId: formData.treatmentId,
            selectedOption: "treatment_paid",
            treatment: formData.description,
          };
        }
      } else {
        if (selectedPaid === "nonpaid") {
          formDatas = {
            ...formData,
            selectedOption: "treatment_non_paid",
          };
        } else {
          if (Number(formData.prices) < 0) {
            setotherserror(true);
            return;
          }
          formDatas = {
            ...buildPaidSubTreatmentPayload(formData),
            selectedOption: "treatment_paid",
          };
        }
      }
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const request = isEdit
      ? baseUrl.patch("/client/treatments/subTreatment/update-subtreetment", {
          ...formDatas,
          ...appointmentIdPayload(appointmentId),
          maintreatmentid: ID,
          treatmentid: TreatmentId,
          subtreatmentid: editItem._id,
        })
      : baseUrl.patch("/client/treatments/subTreatment/add-subtreetment", {
          ...formDatas,
          ...appointmentIdPayload(appointmentId),
          treatment_id: ID,
          sub_treatment_id: TreatmentId,
        });

    request
      .then((response) => {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        if (response?.data?.data?.treatment) {
          setallTeatment(response.data.data.treatment);
        }
        toast.success(
          response?.data?.message ||
            (isEdit ? "Case treatment updated" : "Case treatment added")
        );
        onSaved?.();
        if (inline || inlineRow) {
          resetSubForm();
          onClose?.();
        } else {
          setSubTreatmentOpen(false);
        }
      })
      .catch((error) => {
        console.log(error);
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        toast.error(
          error?.response?.data?.message ||
            (isEdit
              ? "Failed to update case treatment"
              : "Failed to add case treatment")
        );
      });
  };
  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedOption(value);
    if (value === "stages") {
      setformData((prev) => ({ ...prev, prices: 0 }));
    }
  };

  const handlePaidChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPaid(event.target.value);
  };

  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");
  const activeSelectStyles = isDark ? darkSelectStyles : selectStyles;
  const compactInputClass =
    "w-full min-w-0 px-2 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary";
  const rowClass = nested
    ? "bg-white dark:bg-slate-800 align-top"
    : "bg-slate-50/60 dark:bg-primary/10 align-top";

  const inlineSelectStyles = useMemo(
    () => ({
      ...activeSelectStyles,
      control: (base: any, state: any) => ({
        ...activeSelectStyles.control(base, state),
        minHeight: 36,
        height: 36,
      }),
      valueContainer: (base: any) => ({
        ...base,
        paddingTop: 0,
        paddingBottom: 0,
      }),
      indicatorsContainer: (base: any) => ({
        ...base,
        height: 36,
      }),
      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    }),
    [activeSelectStyles]
  );

  const selectWrapClass = "min-w-0 w-full max-w-full";

  const showTypeField =
    selectedOption === "treatment" &&
    (formData.treatment || "").trim().toLowerCase() !== "others" &&
    typeOptions.length > 0;
  const showSubtypeField =
    selectedOption === "treatment" &&
    (formData.treatment || "").trim().toLowerCase() !== "others" &&
    subtypeOptions.length > 0;

  const handleRowSubmit = (e: React.MouseEvent | React.FormEvent) => {
    e.preventDefault();
    onsubmit(e);
  };

  const menuPortalProps = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed" as const,
  };

  if (inlineRow) {
    const fieldLabelClass =
      "block text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1";
    const fieldInputClass =
      "w-full h-9 px-2.5 text-xs rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all";

    const showPriceField =
      selectedOption === "stages" ||
      (selectedOption === "treatment" && selectedPaid === "paid");
    const showQtyField = selectedOption !== "nontreatment";
    const showTreatmentExtraRow =
      selectedOption === "treatment" &&
      (showTypeField || showSubtypeField || openOther);

    const treatmentFieldLabel =
      selectedOption === "stages"
        ? "Stage"
        : selectedOption === "nontreatment"
          ? "Reason"
          : "Treatment";

    const paidToggle = (
      <div className="flex gap-1 p-0.5 bg-slate-100/80 dark:bg-slate-800/80 rounded-lg">
        {[
          { value: "paid", label: "Treatment Cost" },
          { value: "nonpaid", label: "Non Paid" },
        ].map(({ value, label }) => (
          <label
            key={value}
            className={`px-2.5 py-1.5 rounded-md text-[10px] font-semibold cursor-pointer transition-all ${
              selectedPaid === value
                ? "bg-white dark:bg-slate-600 text-primary shadow-sm"
                : "text-slate-600 dark:text-slate-400"
            }`}
          >
            <input
              type="radio"
              value={value}
              checked={selectedPaid === value}
              onChange={handlePaidChange}
              className="sr-only"
            />
            {label}
          </label>
        ))}
      </div>
    );

    return (
      <>
        <tr className={`${rowClass} border-t border-slate-200/80 dark:border-slate-700/50`}>
          <td colSpan={9} className="px-3 py-3">
            <div className="rounded-xl border border-slate-200/90 dark:border-slate-700/80 bg-white/95 dark:bg-slate-900/70 shadow-sm overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2.5 border-b border-slate-100 dark:border-slate-700/80 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800/60 dark:to-slate-900/40">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 dark:bg-primary/20 text-slate-700 dark:text-indigo-300 shrink-0">
                    <MedicalServicesIcon sx={{ fontSize: 16 }} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 leading-tight">
                      {isEdit ? "Edit case treatment" : "New case treatment"}
                    </p>
                    <p
                      className="text-[10px] text-slate-500 dark:text-slate-400 leading-tight truncate max-w-[220px]"
                      title={selectedTreatmentDisplay}
                    >
                      {selectedTreatmentDisplay || "Fill details, then select teeth"}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex gap-1 p-0.5 bg-slate-100/80 dark:bg-slate-800/80 rounded-lg">
                    {tabOptions.map(({ value, label }) => (
                      <label
                        key={value}
                        className={`px-2.5 py-1.5 rounded-md text-[10px] font-semibold cursor-pointer transition-all ${
                          selectedOption === value
                            ? "bg-primary text-white shadow-sm"
                            : "text-slate-600 dark:text-slate-400"
                        }`}
                      >
                        <input
                          type="radio"
                          value={value}
                          checked={selectedOption === value}
                          onChange={handleOptionChange}
                          className="sr-only"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  {selectedOption === "treatment" && paidToggle}
                </div>
              </div>

              <div className="p-3.5 space-y-3">
                <div className="grid grid-cols-12 gap-2.5">
                  <div className="col-span-12 sm:col-span-4 lg:col-span-2">
                    <label className={fieldLabelClass}>Date</label>
                    <input
                      type="date"
                      name="createdAt"
                      value={toLocalDateInputValue(formData.createdAt)}
                      min={minSubDateValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        setformData((prev) => ({
                          ...prev,
                          createdAt: value ? parseLocalDateInput(value) : new Date(),
                        }));
                      }}
                      className={fieldInputClass}
                      title={
                        minSubDateValue
                          ? `Cannot be earlier than main treatment (${minSubDateValue})`
                          : "Treatment date"
                      }
                    />
                  </div>

                  <div
                    className={`min-w-0 ${
                      selectedOption === "stages"
                        ? "col-span-12 sm:col-span-8 lg:col-span-5"
                        : "col-span-12 sm:col-span-8 lg:col-span-4"
                    }`}
                  >
                    <label className={fieldLabelClass}>
                      {treatmentFieldLabel}{" "}
                      {(selectedOption === "stages" ||
                        selectedOption === "nontreatment") && (
                        <span className="text-red-500 normal-case">*</span>
                      )}
                    </label>
                    {selectedOption === "stages" ? (
                      <select
                        value={formData.stage || ""}
                        onChange={(e) =>
                          setformData((prev) => ({
                            ...prev,
                            stage: e.target.value,
                          }))
                        }
                        className={`${fieldInputClass} cursor-pointer appearance-none bg-[length:12px] bg-[right_0.65rem_center] bg-no-repeat pr-8`}
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
                        }}
                      >
                        <option value="">Select stage</option>
                        {stageOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : selectedOption === "nontreatment" ? (
                      <input
                        value={formData.reason}
                        name="reason"
                        type="text"
                        placeholder="Enter reason"
                        onChange={onchange}
                        className={fieldInputClass}
                      />
                    ) : (
                      <div className={selectWrapClass}>
                        <Select
                          isSearchable
                          placeholder="Select treatment"
                          options={treatmentOptions}
                          formatOptionLabel={formatTreatmentOptionLabel}
                          value={
                            treatmentOptions.find(
                              (o) => o.value === formData.treatments
                            ) ?? null
                          }
                          onChange={(option: any) =>
                            option &&
                            onchange({
                              target: {
                                name: "treatments",
                                value: option.value,
                              },
                            })
                          }
                        styles={inlineSelectStyles}
                          classNamePrefix="react-select"
                          {...menuPortalProps}
                        />
                      </div>
                    )}
                    {stageerror &&
                      selectedOption === "stages" &&
                      !formData.stage && (
                        <p className="text-[10px] text-red-500 mt-1">Required</p>
                      )}
                    {reasonerror &&
                      selectedOption === "nontreatment" &&
                      formData.reason === "" && (
                        <p className="text-[10px] text-red-500 mt-1">Required</p>
                      )}
                  </div>

                  {showQtyField ? (
                    <div className="col-span-4 sm:col-span-2 lg:col-span-1">
                      <label className={fieldLabelClass}>Qty</label>
                      <input
                        value={formData.quantity}
                        name="quantity"
                        type="number"
                        min={1}
                        onChange={onchange}
                        className={fieldInputClass}
                      />
                    </div>
                  ) : null}

                  {showPriceField ? (
                    <div className="col-span-8 sm:col-span-4 lg:col-span-2">
                      <label className={fieldLabelClass}>Price</label>
                      <input
                        value={formData.prices ? formData.prices : ""}
                        name="prices"
                        type="number"
                        min={0}
                        placeholder="Optional"
                        onChange={onchange}
                        className={fieldInputClass}
                      />
                      {otherserror && Number(formData.prices) < 0 && (
                        <p className="text-[10px] text-red-500 mt-1">Invalid price</p>
                      )}
                    </div>
                  ) : null}

                  <div
                    className={`col-span-12 ${
                      showQtyField && showPriceField
                        ? "sm:col-span-6 lg:col-span-3"
                        : showQtyField || showPriceField
                          ? "sm:col-span-6 lg:col-span-5"
                          : "sm:col-span-12 lg:col-span-6"
                    }`}
                  >
                    <label className={fieldLabelClass}>Note</label>
                    <input
                      value={formData.note}
                      name="note"
                      type="text"
                      placeholder="Optional note"
                      onChange={onchange}
                      className={fieldInputClass}
                    />
                  </div>
                </div>

                {showTreatmentExtraRow && (
                  <div className="grid grid-cols-12 gap-2.5">
                    {showTypeField && (
                      <div className="col-span-12 sm:col-span-6 min-w-0">
                        <label className={fieldLabelClass}>Type</label>
                        <Select
                          isSearchable
                          placeholder="Select type"
                          options={typeOptions}
                          value={
                            typeOptions.find((o) => o.value === formData.types) ??
                            null
                          }
                          onChange={(option: any) =>
                            option &&
                            onchange({
                              target: { name: "types", value: option.value },
                            })
                          }
                          styles={inlineSelectStyles}
                          classNamePrefix="react-select"
                          {...menuPortalProps}
                        />
                      </div>
                    )}
                    {showSubtypeField && (
                      <div className="col-span-12 sm:col-span-6 min-w-0">
                        <label className={fieldLabelClass}>Subtype</label>
                        <Select
                          isSearchable
                          placeholder="Select subtype"
                          options={subtypeOptions}
                          value={
                            subtypeOptions.find(
                              (o) => o.value === formData.subtypes
                            ) ?? null
                          }
                          onChange={(option: any) =>
                            option &&
                            onchange({
                              target: { name: "subtypes", value: option.value },
                            })
                          }
                          styles={inlineSelectStyles}
                          classNamePrefix="react-select"
                          {...menuPortalProps}
                        />
                      </div>
                    )}
                    {openOther && (
                      <div className="col-span-12 min-w-0">
                        <label className={fieldLabelClass}>
                          Description{" "}
                          <span className="text-red-500 normal-case">*</span>
                        </label>
                        <input
                          value={formData.description}
                          name="description"
                          type="text"
                          placeholder="Enter treatment description"
                          onChange={onchange}
                          className={fieldInputClass}
                        />
                        {otherserror && formData.description === "" && (
                          <p className="text-[10px] text-red-500 mt-1">Required</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {stageUsesProducts && (
                  <StageProductsPicker
                    lines={stageProducts}
                    onChange={setStageProducts}
                    released={releasedStageProducts}
                    selectStyles={inlineSelectStyles}
                    link={stageCategoryLink}
                    scope={{
                      treatmentId: catalogTreatmentId,
                      typeId: catalogTypeId,
                      subtypeId: catalogSubtypeId,
                      stage: String(formData.stage || ""),
                    }}
                  />
                )}

                <div className="pt-1 border-t border-slate-100 dark:border-slate-700/80">
                  <SimpleToothChartPicker
                    value={parseSelectedTeeth(formData)}
                    required
                    error={toothError}
                    onChange={(teeth) => {
                      setToothError(false);
                      setformData((prev) => ({
                        ...prev,
                        ...applySelectedTeeth(teeth),
                      }));
                    }}
                  />
                </div>

                <div className="flex items-center justify-end gap-1.5 pt-1">
                  <button
                    type="button"
                    onClick={handleInlineClose}
                    className="h-8 px-3 text-[11px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-medium whitespace-nowrap hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleRowSubmit}
                    disabled={isSubmitting}
                    className="h-8 px-3 text-[11px] rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-semibold whitespace-nowrap hover:shadow-md hover:shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting
                      ? isEdit
                        ? "Saving…"
                        : "Adding…"
                      : isEdit
                        ? "Save"
                        : "Add"}
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      </>
    );
  }

  const formBody = (
        <form onSubmit={onsubmit} className={inline ? "space-y-3" : "flex-1 overflow-y-auto p-5 space-y-3"}>
          {selectedOption === "stages" ? (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Select Stage *</label>
                <select
                  value={formData.stage || ""}
                  onChange={(e) =>
                    setformData((prev) => ({
                      ...prev,
                      stage: e.target.value,
                    }))
                  }
                  className="w-full h-10 px-3 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 cursor-pointer appearance-none bg-[length:12px] bg-[right_0.75rem_center] bg-no-repeat pr-9 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
                  }}
                >
                  <option value="">Select stage</option>
                  {stageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {stageerror && !formData.stage && (
                  <p className="text-xs text-red-500 mt-0.5">Stage is required</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Price</label>
                <input
                  value={formData.prices === 0 ? "" : formData.prices}
                  name="prices"
                  type="number"
                  min={0}
                  onChange={onchange}
                  placeholder="0"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
                {otherserror && Number(formData.prices) < 0 && (
                  <p className="text-xs text-red-500 mt-0.5">Price cannot be negative</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Note</label>
                <textarea
                  ref={textareaRef}
                  value={formData.note}
                  name="note"
                  onChange={onchange}
                  rows={2}
                  placeholder="Additional details for this stage..."
                  className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                />
              </div>
            </>
          ) : selectedOption === "treatment" ? (
            <>
              {/* Treatment Select */}
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Select Treatment</label>
                <Select
                  isSearchable
                  placeholder="Select treatment"
                  options={treatmentOptions}
                  formatOptionLabel={formatTreatmentOptionLabel}
                  value={treatmentOptions.find((o) => o.value === formData.treatments) ?? null}
                  onChange={(option: any) =>
                    option && onchange({ target: { name: "treatments", value: option.value } })
                  }
                  styles={typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? darkSelectStyles : selectStyles}
                  classNamePrefix="react-select"
                  classNames={{
                    control: () => "!rounded-lg !border-slate-200 dark:!border-slate-600 !bg-white dark:!bg-slate-700",
                    menu: () => "!rounded-lg",
                  }}
                />
                {error && formData.treatments === "" && (
                  <p className="text-xs text-red-500 mt-0.5">Treatment is required</p>
                )}
              </div>

              {/* Type Select */}
              {(formData.treatment || "").trim().toLowerCase() !== "others" && typeOptions.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Select Type</label>
                  <Select
                    isSearchable
                    placeholder="Select type"
                    options={typeOptions}
                    value={typeOptions.find((o) => o.value === formData.types) ?? null}
                    onChange={(option: any) =>
                      option && onchange({ target: { name: "types", value: option.value } })
                    }
                    styles={typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? darkSelectStyles : selectStyles}
                    classNamePrefix="react-select"
                    classNames={{
                      control: () => "!rounded-lg !border-slate-200 dark:!border-slate-600 !bg-white dark:!bg-slate-700",
                      menu: () => "!rounded-lg",
                    }}
                  />
                </div>
              )}

              {/* Subtype Select */}
              {(formData.treatment || "").trim().toLowerCase() !== "others" && subtypeOptions.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Select Subtype</label>
                  <Select
                    isSearchable
                    placeholder="Select subtype"
                    options={subtypeOptions}
                    value={subtypeOptions.find((o) => o.value === formData.subtypes) ?? null}
                    onChange={(option: any) =>
                      option && onchange({ target: { name: "subtypes", value: option.value } })
                    }
                    styles={typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? darkSelectStyles : selectStyles}
                    classNamePrefix="react-select"
                    classNames={{
                      control: () => "!rounded-lg !border-slate-200 dark:!border-slate-600 !bg-white dark:!bg-slate-700",
                      menu: () => "!rounded-lg",
                    }}
                  />
                </div>
              )}

              {/* Price (for paid) — 0 is allowed */}
              {selectedPaid === "paid" && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Price</label>
                  <input
                    value={formData.prices}
                    name="prices"
                    type="number"
                    min={0}
                    onChange={onchange}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                  {otherserror && Number(formData.prices) < 0 && (
                    <p className="text-xs text-red-500 mt-0.5">Price cannot be negative</p>
                  )}
                </div>
              )}

              {/* Quantity */}
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Quantity</label>
                <input
                  value={formData.quantity}
                  name="quantity"
                  type="number"
                  onChange={onchange}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
              </div>

              {/* Description (for Others) */}
              {openOther && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Description *</label>
                  <input
                    value={formData.description}
                    name="description"
                    type="text"
                    onChange={onchange}
                    placeholder="Enter description"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                  {otherserror && formData.description === "" && (
                    <p className="text-xs text-red-500 mt-0.5">Description is required</p>
                  )}
                </div>
              )}
            </>
          ) : (
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Reason *</label>
              <input
                value={formData.reason}
                name="reason"
                type="text"
                onChange={onchange}
                placeholder="Enter reason"
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
              {reasonerror && formData.reason === "" && (
                <p className="text-xs text-red-500 mt-0.5">Reason is required</p>
              )}
            </div>
          )}

          {/* Tooth No & Surface — hidden on Stages tab (included in stages flow above via shared fields) */}
          {selectedOption !== "stages" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Tooth No</label>
              <input
                value={formData.toothNo}
                name="toothNo"
                type="number"
                onChange={onchange}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Surface</label>
              <input
                value={formData.surface}
                name="surface"
                type="text"
                onChange={onchange}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>
          </div>
          )}

          {selectedOption === "stages" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Tooth No</label>
                <input
                  value={formData.toothNo}
                  name="toothNo"
                  type="number"
                  onChange={onchange}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Surface</label>
                <input
                  value={formData.surface}
                  name="surface"
                  type="text"
                  onChange={onchange}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
              </div>
            </div>
          )}

          {/* Note — treatment & non-treatment tabs only */}
          {selectedOption !== "stages" && (
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Note</label>
            <textarea
              ref={textareaRef}
              value={formData.note}
              name="note"
              onChange={onchange}
              rows={2}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
            />
          </div>
          )}

          {/* Submit / Cancel */}
          <div className="flex gap-2">
            {inline && (
              <button
                type="button"
                onClick={handleInlineClose}
                className="flex-1 py-2.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`${inline ? "flex-1" : "w-full"} py-2.5 text-sm rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? "Adding…" : "Add Sub Treatment"}
            </button>
          </div>
        </form>
  );

  const typeToggle = (
    <div className={`${inline ? "mb-3" : "px-6 py-3 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-2 flex-1 min-w-0">
          {tabOptions.map(({ value, label }) => (
            <label
              key={value}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium text-center cursor-pointer transition-all ${
                selectedOption === value
                  ? "bg-gradient-to-r from-primary to-primaryLight text-white shadow-md"
                  : "bg-white dark:bg-slate-600 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600/80"
              }`}
            >
              <input
                type="radio"
                value={value}
                checked={selectedOption === value}
                onChange={handleOptionChange}
                className="sr-only"
              />
              {label}
            </label>
          ))}
        </div>
        {selectedOption === "treatment" && (
          <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-700/50 rounded-lg shrink-0">
            {[
              { value: "paid", label: "Treatment Cost" },
              { value: "nonpaid", label: "Non Paid" },
            ].map(({ value, label }) => (
              <label
                key={value}
                className={`py-1.5 px-3 rounded-md text-xs font-medium text-center cursor-pointer transition-all whitespace-nowrap ${
                  selectedPaid === value
                    ? "bg-white dark:bg-slate-600 text-primary shadow-sm"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                }`}
              >
                <input
                  type="radio"
                  value={value}
                  checked={selectedPaid === value}
                  onChange={handlePaidChange}
                  className="sr-only"
                />
                {label}
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (inline) {
    const formContent = (
      <>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="min-w-0">
            <h4
              className={`text-sm font-semibold ${
                nested
                  ? "text-slate-900 dark:text-slate-300"
                  : "text-slate-700 dark:text-slate-300"
              }`}
            >
              Add Sub Treatment
            </h4>
            <p
              className={`text-xs truncate ${
                nested
                  ? "text-slate-700 dark:text-slate-400"
                  : "text-slate-500 dark:text-slate-400"
              }`}
              title={selectedTreatmentDisplay}
            >
              {selectedTreatmentDisplay}
            </p>
          </div>
          <button
            type="button"
            onClick={handleInlineClose}
            className="w-7 h-7 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-600/60 flex items-center justify-center transition-colors shrink-0"
            title="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>
        {typeToggle}
        {formBody}
      </>
    );

    if (embedded) {
      return formContent;
    }

    if (nested) {
      return (
        <tr className="bg-slate-50/50 dark:bg-slate-900/20">
          <td colSpan={9} className="p-3">
            {formContent}
          </td>
        </tr>
      );
    }

    return (
      <tr className="bg-slate-50/40 dark:bg-primary/10 border-l-2 border-slate-200 dark:border-primary/30">
        <td colSpan={9} className="p-3 pl-8">
          {formContent}
        </td>
      </tr>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 bg-gradient-to-r from-primary to-primaryLight">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <AddCircleIcon className="text-white shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-white">Add Sub Treatment</h2>
                <p
                  className="text-sm text-white/90 mt-1 truncate"
                  title={selectedTreatmentDisplay}
                >
                  <span className="text-white/70 text-xs uppercase tracking-wide font-medium">Selected Treatment · </span>
                  <span className="font-semibold">{selectedTreatmentDisplay}</span>
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSubTreatmentOpen(false)}
              className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors shrink-0"
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </button>
          </div>
        </div>
        {typeToggle}
        {formBody}
      </div>
    </div>
  );
}

export default AddSubTreatmentModel;
